import { AfterViewInit, Component, HostListener, Input, ViewChild } from '@angular/core';
import { InputComponent } from '../input/input.component';
import { DestroyableComponent } from '../../base/destroyable.component';
import { BehaviorSubject, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { TvIcon } from '../icon/icon.enum';

dayjs.extend(customParseFormat);

@Component({
    selector: 'tvcore-datepicker',
    templateUrl: './datepicker.component.html',
    styleUrls: ['./datepicker.component.scss'],
    standalone: false
})
export class DatepickerComponent extends DestroyableComponent implements AfterViewInit {
	@Input()
	label: string;
	@Input()
	icon?: TvIcon | string;
	@Input()
	name: string;
	@Input()
	showErrors: boolean = false;

	@ViewChild(InputComponent)
	inputComponent: InputComponent;

	date: dayjs.Dayjs | null;
	days$: BehaviorSubject<Day[][] | null> = new BehaviorSubject<Day[][] | null>(null);
	visibleDate: dayjs.Dayjs;
	view: 'day' | 'month' = 'day';
	showPicker: boolean = false;

	constructor() {
		super();
	}

	get title(): string {
		switch (this.view) {
			case 'day':
				return this.visibleDate?.format('MMMM YYYY');
			case 'month':
				return this.visibleDate?.format('YYYY');
		}
	}

	static convertValueToDate(value: string | null): dayjs.Dayjs | null {
		if (!value) {
			return null;
		} else {
			return dayjs(value, ['DD/MM/YYYY', 'DD-MM-YYYY'], true);
		}
	}

	static convertDateToISO(date: dayjs.Dayjs | null): string | null {
		return date?.isValid() ? date?.format('YYYY-MM-DDTHH:mm:ss[Z]') : null;
	}

	static convertDateToValue(date: dayjs.Dayjs | null): string | null {
		return date?.isValid() ? date?.format('DD/MM/YYYY') : null;
	}

	ngAfterViewInit(): void {
		if (this.inputComponent?.control) {
			if (this.inputComponent.control.updateOn !== 'blur') {
				console.error('Datepicker should update on blur.');
			}

			const now = (this.inputComponent.control.value ? DatepickerComponent.convertValueToDate(this.inputComponent.control.value) : null) || dayjs();
			this.showMonth(now.get('month') + 1, now.get('year'));

			this.inputComponent.control.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
				this.validateDate(value);
			});
		}
	}

	@HostListener('document:keydown.escape')
	closePicker() {
		this.showPicker = false;

		const d = this.date?.isValid() ? this.date : dayjs();
		const m = d.get('month');
		const y = d.get('year');

		this.showMonth(m + 1, y);

		this.view = 'day';
	}

	hasError(name: string): boolean {
		return !!this.inputComponent.control?.hasError(name);
	}

	isSelected(day: Day): boolean {
		return this.equalDates(day.date, this.date || null);
	}

	next() {
		let d = dayjs(this.visibleDate);
		d = d.add(1, this.view === 'day' ? 'month' : 'year');
		this.showMonth(d.get('month') + 1, d.get('year'));
	}

	openPicker() {
		this.showPicker = true;
	}

	prev() {
		let d = dayjs(this.visibleDate);
		d = d.add(-1, this.view === 'day' ? 'month' : 'year');
		this.showMonth(d.get('month') + 1, d.get('year'));
	}

	select(day: Day) {
		const value = DatepickerComponent.convertDateToValue(day.date);
		this.inputComponent.control?.setValue(value);
		this.showPicker = false;
	}

	setMonth(month: number) {
		const year = this.visibleDate.year();
		this.showMonth(month, year);
		this.view = 'day';
	}

	toggleView() {
		this.view = this.view === 'day' ? 'month' : 'day';
	}

	private validateDate(value: string | null) {
		this.date = DatepickerComponent.convertValueToDate(value);

		if (this.date) {
			if (!this.date.isValid()) {
				this.inputComponent.control?.setErrors({ format: true });
			} else {
				this.showMonth(this.date.get('month') + 1, this.date.get('year'));
			}
		}
	}

	private showMonth(month: number, year: number) {
		this.visibleDate = dayjs(`01/${month > 9 ? month : `0${month}`}/${year} 12:00:00`, 'DD/MM/YYYY hh:mm:ss');

		let start = dayjs(this.visibleDate);
		let s = start.get('day');

		if (s === 0) {
			s = 7;
		}

		start = start.add(-1 * (s - 1), 'day');

		const days = [];

		for (let i = 0; i <= 5; i++) {
			days.push(this.addRow(start.add(7 * i, 'day')));
		}

		this.days$.next(days);
	}

	private addRow(date: dayjs.Dayjs): Day[] {
		const row = [];
		const today = dayjs();

		for (let i = 0; i < 7; i++) {
			const d = date.add(i, 'day');
			const isToday = this.equalDates(d, today);

			row.push({
				date: d,
				text: d.format('DD'),
				visible: d.get('month') === this.visibleDate.get('month'),
				isToday,
			});
		}

		return row;
	}

	private equalDates(d1: dayjs.Dayjs | null, d2: dayjs.Dayjs | null): boolean {
		return d1?.format('YYYYMMDD') === d2?.format('YYYYMMDD');
	}
}

class Day {
	date: dayjs.Dayjs;
	text: string;
	visible: boolean;
	isToday: boolean;
}
